import MobileNav from "./MobileNav";
import Header from "./Header";
import { Outlet } from "react-router-dom";
import OnboardingWizard from "../training/OnboardingWizard";
import ContextHelp from "../training/ContextHelp";
import BugReporter from "../shared/BugReporter";
import { useState } from "react";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Keyboard, CalendarDays, ArrowRight } from "lucide-react";
import { useFY } from "../../context/FYContext";
import { useCompany } from "../../context/CompanyContext";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { trackRecentPage } from "../../lib/navModules";

function FYBanner() {
  const { currentFY, selectedFY, selectedLabel, changeFY, fyLabel } = useFY();
  if (!selectedFY || !currentFY) return null;
  const isHistorical = selectedFY !== currentFY;

  if (!isHistorical) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-amber-700 dark:text-amber-400"
      data-testid="fy-banner"
    >
      <div className="flex items-center gap-2 text-xs font-medium">
        <CalendarDays className="w-3.5 h-3.5 shrink-0" />
        <span>Viewing data for <strong>FY {selectedLabel}</strong></span>
      </div>
      <button
        onClick={() => changeFY(currentFY)}
        className="flex items-center gap-1 text-[11px] font-semibold hover:underline whitespace-nowrap"
        data-testid="fy-switch-current-btn"
      >
        Switch to FY {fyLabel} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { showHelp, setShowHelp, SHORTCUTS } = useKeyboardShortcuts();
  const { selectedFY } = useFY();
  const { activeCompany } = useCompany();
  const location = useLocation();

  useEffect(() => { trackRecentPage(location.pathname); }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Mobile nav overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar-overlay">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative z-50 w-72 max-w-[80vw]">
            <MobileNav onClose={() => setMobileMenuOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden bg-background min-w-0">
        <Header onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} onShortcutsHelp={() => setShowHelp(true)} />
        <FYBanner />
        <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6" data-testid="main-content">
          <Outlet key={`${selectedFY}-${activeCompany?.id || ""}`} />
        </main>
      </div>
      <OnboardingWizard />
      <ContextHelp />
      <BugReporter />

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-md" data-testid="keyboard-shortcuts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5" /> Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-3">
            {SHORTCUTS.map(s => (
              <div key={s.key} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50">
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <kbd className="px-2 py-0.5 rounded bg-background border text-xs font-mono font-semibold">Alt+{s.key.toUpperCase()}</kbd>
              </div>
            ))}
            <div className="col-span-2 flex items-center justify-between px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
              <span className="text-sm font-medium">Toggle this help</span>
              <kbd className="px-2 py-0.5 rounded bg-background border text-xs font-mono font-semibold">Alt+?</kbd>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
